import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';

import { ModalComponent } from '../shared/modal/modal.component';
import { AddContactComponent } from '../shared/modal/add-contact/add-contact.component';
import { FilterComponent } from '../shared/components/filter/filter.component';
import { GridColumn, ListComponent } from '../shared/components/list/list.component';
import { ContactListService } from 'com.trumbull.phx.contactlist.api-client';
import { DialogModule } from 'primeng/dialog';
import { catchError, of, tap } from 'rxjs';
import { VocabHelperService } from '../services/vocab-helper/vocab-helper.service';
import { LoaderService } from '../services/loader-service/loader.service';
import { stat } from 'fs';
import { LoaderComponent } from '../loader/loader.component';
import { ContactHelperService } from '../services/contact-helper/contact-helper.service';

export interface FilterField {
  type: 'select' | 'multiselect' | 'text';
  label: string;
  name: string;
  placeholder?: string;
  options: any[];
  value: any;
  optionLabel?: string;  // Add this property
  optionValue?: string;  // Add this property
  maxSelectedLabels?: number;
  class?: string;
  onChange?: () => void;
}

export interface Contact {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  extn?: string | number;
  accountName?: string;
  status?: string;
  barNumber?: string;
  phonePartyLocatorKeys?: any[];
  emailPartyLocatorKeys?: any[];
}

export interface TableParams {
  first: number;
  rowsPerPage: number;
  sortField: string;
  sortOrder: number;
}

export interface OrdersListFilter {
  accountName: string[];
  title: string [];
  status: string[];
  name?: string | null;
}

@Component({
  selector: 'app-account-manager-contacts',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    AddContactComponent,
    FilterComponent,
    ListComponent,
    DialogModule,
    LoaderComponent
  ],
  templateUrl: './account-manager-contacts.component.html',
  styleUrl: './account-manager-contacts.component.scss'
})
export class AccountManagerContactsComponent implements OnInit {
  @ViewChild(AddContactComponent) addContactComp!: AddContactComponent;

  // Modal Properties
  @ViewChild('addContactComp') addContactComponent!: AddContactComponent;
  visible: boolean = false;
  dataFlag: boolean = false;

  // Data Properties
  contacts: Contact[] = [];
  totalRecords = 0;
  loading = false;
  panelCollapsed: boolean = false;
  externalUser: boolean = false;
  titles: any[] = [];
  accountNames: any = [];
  statuses: any = [];
  providerId: string = "standard";
  accountType: string = "account";
  listId: string = "";
  contactList: any = [];

  contactTitleVocab: any;
  contactStatusVocab: any;
  accountNameVocab: any;
  addContactHeader: string = 'Add Contact';
  isEditModeEnabled: boolean = false;
  originalContactList: Contact[] = [];

  // Table Configuration
  tableParams: TableParams = {
    first: 0,
    rowsPerPage: 10,
    sortField: 'name',
    sortOrder: 1
  };

  // UI Configuration
  readonly customLabels = {
    account: 'Account Name',
    name: 'Name',
    title: 'Title',
    email: 'Email',
    phone: 'Phone Number',
    extension: 'Extension',
    status: 'Status',
    barNumber: 'Bar Number'
  };

  readonly customVisibility = {
    name: true,
    phone: true,
    email: true,
    account: true,
    title: true,
    barNumber: true,
    extension: true,
    status: true
  };

  readonly customPlaceholders = {
    account: 'Select account name',
    name: 'Enter name',
    title: 'Select title',
    extension: '--',
    status: 'Select status',
    phone: 'Enter phone',
    email: 'Enter email',
    barNumber: "Enter attorney's bar number"
  };

  // Filter Configuration
  ordersListFilter: OrdersListFilter = {
    accountName: [],
    title: [],
    status: [],
    name: null
  };

  // Grid Configuration
  readonly columns: GridColumn[] = [
    { field: 'name', header: 'Name', isSortable: true },
    { field: 'title', header: 'Title', isSortable: true },
    { field: 'email', header: 'Email', isSortable: true },
    {
      field: 'phone',
      header: 'Phone & Extn',
      isSortable: true,
      // customRender: (row: Contact) => `${row.phone}${row.extn ? ', Ext. ' + row.extn : ''}`
      customRender: (row: Contact) => {
        const phone = row.phone || '';
        const extn = row.extn ? String(row.extn).trim() : '';

        if (!phone && !extn) return '--';
        if (!phone) return `extn. ${extn}`;
        if (!extn) return phone;
        return `${phone} extn. ${extn}`;
      }
    },
    { field: 'barNumber', header: 'Bar Number', isSortable: true },
    { field: 'accountName', header: 'Account Name', isSortable: true },
    { field: 'status', header: 'Status', isSortable: true }
  ];

  // Initialize filterFields in ngOnInit to avoid reference issues
  filterFields: FilterField[] = [];

  constructor(
    private contactListService: ContactListService,
    private vocabHelperService: VocabHelperService,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    private readonly contactHelperService: ContactHelperService
  ) { }

  // Lifecycle Methods
  ngOnInit(): void {
    
    // Load vocabulary data first, then initialize filter fields
    this.getDataToInitialize();
    
    // Load contact data
    this.loadData();
    
    // Initialize filter fields will be called after vocabulary data is loaded
    // this.initializeFilterFields(); // Remove this line since it's now called in getDataToInitialize()
  }

  // Initialization Methods
  private initializeFilterFields(): void {
    this.filterFields = [
      {
        type: 'multiselect',
        label: 'Account Name',
        name: 'accountName',
        placeholder: 'Select account name',
        options: this.accountNames, // This will be populated from getDataToInitialize()
        value: this.ordersListFilter.accountName,
        optionLabel: 'value', // Add this to specify the display property
        optionValue: 'code',  // Add this to specify the value property
        onChange: () => this.onSelectAccountName()
      },
      {
        type: 'multiselect',
        label: 'Title',
        name: 'title',
        placeholder: 'Select title',
        options: this.titles, // Pass the titles array here
        value: this.ordersListFilter.title,
        optionLabel: 'value', // Add this to specify the display property
        optionValue: 'code'   // Add this to specify the value property
      },
      {
        type: 'multiselect',
        label: 'Status',
        name: 'status',
        placeholder: 'Select status',
        options: this.statuses, // This will be populated from getDataToInitialize()
        value: this.ordersListFilter.status,
        optionLabel: 'value', // Add this to specify the display property
        optionValue: 'code',  // Add this to specify the value property
      },
      {
        type: 'text',
        label: 'Name',
        name: 'name',
        placeholder: 'Enter name',
        options: [],
        value: this.ordersListFilter.name
      }
    ];
  }

  // Modal Methods
  onCancelForm(): void {    
    this.contacts = [...this.originalContactList];
    this.isEditModeEnabled = false;
    this.addContactHeader = 'Add Contact';
    
    // Reset form without affecting table data
    if (this.addContactComponent) {
      this.addContactComponent.resetForm();
      this.addContactComponent.isEditModeEnabled = false;
      this.addContactComponent.isAccountNameDisabled = false;
    }
    this.cdr.detectChanges();
    // Close modal
    this.visible = false;
  }

  onOpenAddContactDialog(): void {
    this.onCancelForm();
    this.visible = true;
  }

  onDialogHide(): void {
    this.onCancelForm();
  }

  onAddContact(): void {
    this.isEditModeEnabled = false;
    this.addContactHeader = 'Add Contact';
    // Mark all fields as touched to show validation errors
    this.addContactComponent.markTouched();

    // Check if form is valid
    if (this.addContactComponent.isValid()) {
      // Get form data
      const contactData = this.addContactComponent.getFormData();

      // Process the data (save to service, API call, etc.)
      this.saveContact(contactData);

      // Close the dialog
      this.visible = false;

      // Reset the form for next use
      this.addContactComponent.resetForm();
    } else {
      console.warn('Form is invalid. Please check required fields.');
    }
  }

  private saveContact(contactData: any): void {
    this.listId = this.contactList.find((list: any) => list.name === contactData.accountName.code)?.id || "";
    
    const party: any = this.getPartyCreated(contactData);
    
    this.contactListService.createContact(this.providerId, this.listId, party).subscribe({
      next: (response) => {
        this.loadData(); // Refresh the list
      },
      error: (error) => {
        console.error('Error saving contact:', error);
      }
    });
  }

  getFirstName(fullName: string): string {
    return this.contactHelperService.getFirstName(fullName);
  }

  getLastName(fullName: string): string {
    return this.contactHelperService.getLastName(fullName);
  }

  onSaveAndAddContact(): void {
    this.onAddContact();
    // if (this.validateAndProcessContact()) {
    //   this.closeModalAndReset();
    // }
    this.visible = true; // Keep the modal open for adding another contact
  }

  // Filter Methods to be implemented later
  onSelectAccountName(): void {
    const selectedField = this.filterFields.find(field => field.name === 'accountName');
    if (selectedField) {
    }
  }

  applyFilters(event: any): void {
    const filterValues = event; // Get the filter values from the event
    const accountNames = this.getAccountNames(filterValues.accountName);
    const contactName = filterValues.name;
    const status = this.getStatusCodes(filterValues.status);
    let title = this.getTitleValues(filterValues.title);
    this.listId = this.getListIdByAccountNames(filterValues.accountName);
    this.loaderService.show();
    if (!title) {
      title = this.titles.map(t => t.code).join(',');
    }
    // Call API with filter values
    this.contactListService.searchContacts(
                  this.providerId, // providerId
                  this.listId, // contactListId
                  contactName, // contactName
                  accountNames, // accountNames
                  '', // clientNames
                  status, // status
                  '', // partyid
                  title, // tagName
                  '', // contactId
                  'Person', // typeCode
                  '', // sort
                  undefined, // page
                  undefined // pageSize
              ).subscribe({
      next: (data: any) => {
        this.loaderService.hide();
        this.contacts = []; // Clear existing contacts before loading new data
        data.contactList.forEach((list: any) => {
          this.contactList.push({ id: list.id, name: list.name });
          if (list.contactItems && list.contactItems.length) {
            this.mapApiDataToContacts(list.contactItems, list.name);
          }
          // else {
          //   console.warn("No contact items found in response");
          //   this.contacts = [];
          // }
        });
        this.totalRecords = this.contacts.length;
        this.loading = false;
      },
    });
    // this.loadData();
  }

  getAccountNames(selectedAccounts: any[]): string {
    // If there are selected account names in the filter, return only those
    let accountNames: string = "";
    if (selectedAccounts && selectedAccounts.length > 0) {
      selectedAccounts.forEach(accountCode => {
        accountNames === "" ? accountNames = accountCode : accountNames += "," + accountCode;

      });
    }
    return accountNames;
  }

  getStatusCodes(selectedStatuses: any[]): string {
    // If there are selected status in the filter, return only those
    let statuses: string = "";
    if (selectedStatuses && selectedStatuses.length > 0) {
      selectedStatuses.forEach(statusCode => {
        statuses === "" ? statuses = this.getStatus(statusCode) : statuses += "," + this.getStatus(statusCode);
      });
    }
    return statuses;
  }

  getStatus(code: string): string {
    if (code.toLowerCase() === 'active') {
      return 'true';
    } else if (code.toLowerCase() === 'inactive') {
      return 'false';
    }
    return code;
  }

  getTitleValues(selectedTitles: any[]): string {
    // If there are selected titles in the filter, return only those
    let titles: string = "";
    if (selectedTitles && selectedTitles.length > 0) {
      selectedTitles.forEach(titleCode => {
        titles === "" ? titles = titleCode : titles += "," + titleCode;
      });
    }
    return titles;
  }

  getListIdByAccountNames(selectedAccounts: any[]): string {
    // If there are selected account names in the filter, return only those
    let listId: string = "";
    if (selectedAccounts && selectedAccounts.length > 0) {
      selectedAccounts.forEach(accountCode => {
        listId === "" ? listId = this.getListId(accountCode) : listId += "," + this.getListId(accountCode);
      });
    }
    return listId;
  }

  getListId(accountCode: string): string {
    return this.contactList.find((list: any) => list.name === accountCode)?.id || "";
  }

  resetFilters(): void {
    this.loadData();
    this.ordersListFilter = {
      accountName: [],
      title: [],
      status: [],
      name: null
    };

    this.updateFilterFieldValues();
  }

  // Table Methods
  loadData(event: any = null): void {
    this.loading = true;

    try {
      // Update table params if event is provided
      if (event) {
        this.updateTableParams(event);
      }

      this.getContactData(event); // Pass event to preserve sorting
    } catch (error) {
      console.error('Error loading data:', error);
      this.loading = false;
    } finally {
      // this.loaderService.hide();
    }
  }

  onPageChange(event: any): void {
    this.loadData(event);
  }

  // Private Helper Methods
  private validateAndProcessContact(): boolean {
    if (!this.addContactComp) {
      console.error('AddContactComponent is not available');
      return false;
    }

    try {
      // Type-safe method calls with error handling
      if (typeof this.addContactComp.markTouched === 'function') {
        this.addContactComp.markTouched();
      }

      if (typeof this.addContactComp.isValid === 'function' && this.addContactComp.isValid()) {
        if (typeof this.addContactComp.getFormData === 'function') {
          const contactData = this.addContactComp.getFormData();

          // Add the new contact to the list
          this.contacts.unshift(contactData);
          this.totalRecords = this.contacts.length;

          return true;
        }
      }
    } catch (error) {
      console.error('Error validating contact:', error);
      return false;
    }

    console.warn('Contact form is invalid.');
    return false;
  }

  private closeModalAndReset(): void {
    this.visible = false;
    if (this.addContactComp && typeof this.addContactComp.onCancel === 'function') {
      this.addContactComp.onCancel();
    }
  }

  private updateTableParams(event: any): void {
    this.tableParams = {
      first: event?.first || 0,
      rowsPerPage: event?.rows || 10,
      sortField: event?.sortField || 'name',
      sortOrder: event?.sortOrder || 1
    };
  }

  private updateFilterFieldValues(): void {
    this.filterFields.forEach(field => {
      switch (field.name) {
        case 'accountName':
        case 'status':
          field.value = [];
          break;
        case 'title':
          field.value = null;
          break;
      }
    });
  }

  // Implementation of Get Contact Data
  getContactData(event: any = null) {
    this.loading = true;
    let contactItems: any[] = [];

    this.loaderService.show();
    // this.contactListService.searchContacts(this.providerId, "8").subscribe({
    this.contactListService.listContactLists(this.providerId, "", "", this.accountType).subscribe({
      next: (data: any) => {
        this.loaderService.hide();
        this.contacts = []; // Clear existing contacts before loading new data
        data.contactList.forEach((list: any) => {
          this.contactList.push({ id: list.id, name: list.name });
          if (list.contactItems && list.contactItems.length) {
            this.mapApiDataToContacts(list.contactItems, list.name);
          }
          // else {
          //   console.warn("No contact items found in response");
          //   this.contacts = [];
          // }
        });
        this.totalRecords = this.contacts.length;
        this.loading = false;
      },
      error: (error) => {
        console.error("Error fetching contacts:", error);
        this.contacts = [];
        this.totalRecords = 0;
        this.loading = false;
      }
    });
  }

  mapApiDataToContacts(apiContacts: any, acctName: string) {
    // this.contacts = [];

    apiContacts.forEach((item: any) => {
      // Initialize variables for EACH contact (moved inside forEach)
      let name = '';
      let title = '';
      let email = '';
      let accountName = this.getAccountFullName(acctName);
      let status = '';
      let barNumber = '';
      let phonePartyLocatorKeys: any[] = [];
      let emailPartyLocatorKeys: any[] = [];

      const contactCore = this.contactHelperService.extractContactCore(item);

      // Extract basic info
      name = contactCore.displayName;
      status = contactCore.isActive ? 'Active' : 'Inactive';
      email = contactCore.email;
      barNumber = contactCore.barNumber;
      phonePartyLocatorKeys = contactCore.phonePartyLocatorKeys;
      emailPartyLocatorKeys = contactCore.emailPartyLocatorKeys;

      title = this.getTagNames(item.tags);

      // Push the individual contact to the array
      this.contacts.push({
        id: item.id,
        name: name,
        title: title,
        email: email ? email : "--",
        phone: this.contactHelperService.formatPhoneDisplay(contactCore.workPhone.number, contactCore.workPhone.extension),
        // extn: extn,
        accountName: accountName,
        status: status,
        barNumber: barNumber ? barNumber : "--",
        phonePartyLocatorKeys: phonePartyLocatorKeys,
        emailPartyLocatorKeys: emailPartyLocatorKeys
      });
    });
    this.originalContactList = [...this.contacts];
  }

  private getTagNames(tags: any[]): string {
    return tags.map(tag => this.getTitleValue(tag.tagName)).join(", ");
  }

  private getAccountFullName(acctName: string): string {
    return this.accountNameVocab.vocabularyItems.find((item: any) => item.code === acctName)?.description || acctName;
  }

  private getTitleValue(code: string): string {
    return this.contactTitleVocab.vocabularyItems.find((item: any) => item.code === code)?.description || code;
  }

  getDataToInitialize(): void {
    this.vocabHelperService.getVocabData(['ContactTitle', 'UserOrganization', 'ContactStatusCode']).subscribe(
      data => {
        // Process ContactTitle
        this.contactTitleVocab = data.find(d => d.vocabularyName === 'ContactTitle');
        if (this.contactTitleVocab && this.contactTitleVocab.vocabularyItems) {
          this.titles = []; // Reset the array
          this.contactTitleVocab.vocabularyItems.forEach((item: any) => {
            this.titles.push({ code: item.code, value: item.description });
          });
          
          this.titles = this.titles.sort((a: any, b: any) => {
            const aIsOthers = a.value.toLowerCase() === 'other';
            const bIsOthers = b.value.toLowerCase() === 'other';
            
            if (aIsOthers && !bIsOthers) return 1;
            if (!aIsOthers && bIsOthers) return -1;
            
            return a.value.localeCompare(b.value);
          });
        }

        // Process UserOrganization
        this.accountNameVocab = data.find(d => d.vocabularyName === 'UserOrganization');
        if (this.accountNameVocab && this.accountNameVocab.vocabularyItems) {
          this.accountNames = []; // Reset the array
          this.accountNameVocab.vocabularyItems.forEach((item: any) => {
            this.accountNames.push({ code: item.code, value: item.description });
          });
        }

        // Process ContactStatusCode
        this.contactStatusVocab = data.find(d => d.vocabularyName === 'ContactStatusCode');
        if (this.contactStatusVocab && this.contactStatusVocab.vocabularyItems) {
          this.statuses = []; // Reset the array
          this.contactStatusVocab.vocabularyItems.forEach((item: any) => {
            this.statuses.push({ code: item.code, value: item.description });
          });
        }

        // Re-initialize filter fields after data is loaded
        this.initializeFilterFields();
        
      },
      error => {
        console.error('Error loading vocabulary data:', error);
      }
    );
  }

  // Add this method to handle name clicks
  onContactNameClick(event: any): void {
    this.isEditModeEnabled = true;
    let eventData = event.data;
    eventData.id = event.data.id;
    eventData.accountName = { code: this.getAccountCode(eventData.accountName), value: eventData.accountName };
    eventData.status = { code: eventData.status.toLowerCase(), value: eventData.status };
    eventData.title = { code: eventData.title.toLowerCase(), value: eventData.title };
    let phoneNo = eventData.phone;
    eventData.phone = phoneNo.split(' extn. ')[0];
    eventData.extension = phoneNo.split(' extn. ')[1];

    // eventData.forEach((item: any) => {
    //   item.key === 'accountName' ? item.value = this.getAccountCode(item.value) : null;
    //   item.key === 'status' ? item.value = item.value.toLowerCase() : null;
    //   item.key === 'title' ? item.value = item.value.toLowerCase() : null;
    // });
    const contactData = event.data;
    const rowIndex = event.index;
    
    // You can implement different actions here:
    
    // Option 1: Navigate to contact details page
    // this.router.navigate(['/contact-details', contactData.id]);
    
    // Option 2: Open contact in edit mode
    this.editContact(contactData);
    
    // Option 3: Show contact details in a modal
    // this.showContactDetails(contactData);
    
    // Option 4: Log for debugging
  }

  getAccountCode(acctName: string): string {
    return this.accountNameVocab.vocabularyItems.find((item: any) => item.description === acctName)?.code || acctName;
  }

  // Example method to edit contact
  editContact(contactData: any): void {
    // Populate the add contact form with existing data for editing
    this.addContactHeader = 'Edit Contact';
    this.visible = true;
    
    // Wait for the component to be available
    setTimeout(() => {
      if (this.addContactComponent) {
        this.addContactComponent.isEditModeEnabled = true;
        this.addContactComponent.setFormData(contactData);
        
        // Disable account name field in edit mode
        this.addContactComponent.disableAccountNameField();
      }
    }, 100);
    
  }

  onUpdateContact(): void {
    // Mark all fields as touched to show validation errors
    this.addContactComponent.markTouched(); 

    // Check if form is valid
    if (this.addContactComponent.isValid()) {
      // Get form data
      const contactData = this.addContactComponent.getFormData();

      // Process the data (save to service, API call, etc.)
      this.updateContact(contactData);

      // Close the dialog
      this.visible = false;

      // Reset the form for next use
      this.addContactComponent.resetForm();
    } else {
      console.warn('Form is invalid. Please check required fields.');
    }
  }

  updateContact(contactData: any): void {
    this.listId = this.contactList.find((list: any) => list.name === contactData.accountName.code)?.id || "";
    
    const contactId = contactData.id;

    const party: any = this.getPartyCreated(contactData);
    
    this.contactListService.updateContact(this.providerId, this.listId, contactId, party).subscribe({
      next: (response) => {
        this.loadData(); // Refresh the list
      },
      error: (error) => {
        console.error('Error updating contact:', error);
      }
    });
    this.isEditModeEnabled = false;
    this.addContactHeader = 'Add Contact';
  }

  getPartyCreated(contactData: any): any {
    return this.contactHelperService.buildContactPartyPayload({
      displayName: contactData.name,
      titleCode: contactData.title.code,
      barNumber: contactData.barNumber,
      phone: contactData.phone,
      extension: contactData.extension,
      email: contactData.email,
      phonePartyLocatorKeys: this.getPhonePartyLocatorKeys(contactData.id),
      emailPartyLocatorKeys: this.getEmailPartyLocatorKeys(contactData.id),
      active: contactData.status.toLowerCase() === 'active' ? true : false
    });
  }

  getPhonePartyLocatorKeys(contactId: string): any[] {
    return this.originalContactList.find(contact => contact.id === contactId)?.phonePartyLocatorKeys || [];
  }

  getEmailPartyLocatorKeys(contactId: string): any[] {
    return this.originalContactList.find(contact => contact.id === contactId)?.emailPartyLocatorKeys || [];
  }
}

